import { createSelector } from 'reselect';
import { IAppState, IPolicy, IPolicyScan } from 'types';
import { selectReferenceState } from './referenceSelectors';
import { selectUser } from './userSelectors';
import {
  dateLessThanOrEqualToDays,
  isFamilyOwner,
  isMarketScanPolicy,
  isPolicyInReview,
} from 'helpers';
import { RENEWAL_START_DAYS } from 'constants/policy-details';
import { PolicyStatus } from 'constants/policy';
import { FREQUENCY } from 'constants/options';

const defaultPolicy = {};

const getIdArg = (_, id: string) => id;

export const selectCreditCardState = (state: IAppState) => state.creditCard;

export const selectPoliciesState = (state: IAppState) => state.policies;

export const selectPolicyById = createSelector(
  selectPoliciesState,
  getIdArg,
  ({ policies, selectedPolicy }, policyId): IPolicy => {
    const policy = policies.find(R.propEq('id', policyId));
    return policy || selectedPolicy || defaultPolicy;
  },
);

export const msWaitingSelectPolicyById = (
  state: IAppState,
  policyId: string,
) => {
  return state.policies.policies.find((p) => p.id === policyId);
};

export const policiesToLinkSelector = createSelector(
  R.path(['policies', 'policies']),
  (policies: IPolicy[]): IPolicy[] => {
    return policies.filter(R.prop('needConfirmPurchase'));
  },
);

export const selectPolicyDetails = createSelector(
  selectPolicyById,
  selectReferenceState,
  selectPoliciesState,
  ({ policyRenewal }) => policyRenewal,
  selectUser,
  getIdArg,
  (
    policy,
    { employers, policyProviders },
    { loading },
    { pendingRenewals, renewals, selectedRenewal, submitted: renewalSubmitted },
    user,
    policyId,
  ) => {
    const isOwner = isFamilyOwner(user);
    const isCreditCard = (policyId || policy?.id)?.includes('credit-card');

    return {
      allowMarketScan: isOwner && isMarketScanPolicy(policy),
      employer: employers.find(R.propEq('id', policy.employerId)),
      isCreditCard,
      isOwner,
      loading: !isCreditCard && (loading || R.isEmpty(policy)),
      policy,
      policyProvider: policyProviders.find(
        R.propEq('id', policy.policyProviderId),
      ),
      pendingRenewal:
        !renewalSubmitted && pendingRenewals.find(R.propEq('sk', policy?.id)),
      renewals,
      showComparison:
        policy?.frequency !== FREQUENCY.ONE_OFF &&
        !!policy?.premium &&
        (policy?.status === PolicyStatus.ACTIVE ||
          policy?.status === PolicyStatus.MARKET_SCAN_DRAFT ||
          policy?.status === PolicyStatus.INCOMPLETE),
      isReview: !policy?.processed || isPolicyInReview(policy),
      showRenewal:
        isOwner &&
        policy.renewalDate &&
        policy.frequency !== FREQUENCY.ONE_OFF &&
        !policy.policyRenewalAdded &&
        dateLessThanOrEqualToDays(RENEWAL_START_DAYS, policy.renewalDate),
      selectedRenewal,
      user,
    };
  },
);

export const selectCreditCardDetails = createSelector(
  selectCreditCardState,
  selectReferenceState,
  selectUser,
  getIdArg,
  ({ creditCards, loaded }, { creditCards: creditCardsRef }, user, ccId) => {
    const policy = creditCards.find(R.propEq('id', ccId));
    return {
      policy,
      creditCardInfo: creditCardsRef.find(R.propEq('id', policy?.creditCardId)),
      isOwner: isFamilyOwner(user),
      loading: !policy || !loaded,
    };
  },
);
